import React, { useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../translate/i18n";
import ReportTable from "./ReportTable";
import ChannelDonut from "./ChannelDonut";
import { channelLabel, formatDuration, percent } from "./format";

const useStyles = makeStyles((theme) => ({
  grid: { display: "grid", gap: 16 },
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: "16px 18px",
  },
  title: { fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 12 },
  status: { display: "inline-flex", alignItems: "center", gap: 6 },
  dot: { width: 8, height: 8, borderRadius: 4 },
}));

const STATUS_COLOR = { CONNECTED: "#16A34A", DISCONNECTED: "#DC2626" };

/**
 * Pestaña «Canales y conexiones» (doc 0447 §5 y §10.4): estado y rotación de las
 * conexiones DE LA EMPRESA. Es lo que el administrador antes solo veía mezclado con
 * cifras de toda la plataforma en «Conexiones».
 */
const ChannelsTab = ({ data }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);

  const rows = useMemo(() => {
    const stats = new Map((data.byConnection || []).map((c) => [String(c.id), c]));
    const list = (whatsApps || []).map((w) => {
      const s = stats.get(String(w.id)) || {};
      return {
        id: w.id,
        name: w.name,
        channel: channelLabel(w.provider),
        status: w.status,
        conversations: s.conversations || 0,
        resolved: s.resolved || 0,
        avgResolutionSeconds: s.avgResolutionSeconds ?? null,
      };
    });
    // Conexiones ya borradas que tuvieron conversaciones en el periodo.
    (data.byConnection || []).forEach((c) => {
      if (!list.some((r) => String(r.id) === String(c.id))) {
        list.push({ ...c, name: c.label || `#${c.id}`, channel: "—", status: null });
      }
    });
    return list;
  }, [whatsApps, data.byConnection]);

  const statusCell = (r) => {
    if (!r.status) return "—";
    const key = String(r.status).toUpperCase();
    return (
      <span className={classes.status}>
        <span className={classes.dot} style={{ backgroundColor: STATUS_COLOR[key] || "#F59E0B" }} />
        {i18n.t(`reports.channels.status.${key}`, { defaultValue: r.status })}
      </span>
    );
  };

  const columns = [
    { key: "name", label: i18n.t("reports.channels.connection"), sort: (r) => String(r.name || "") },
    { key: "channel", label: i18n.t("reports.channels.channel"), sort: (r) => String(r.channel || "") },
    { key: "status", label: i18n.t("reports.channels.statusLabel"), render: statusCell, sort: (r) => String(r.status || "") },
    { key: "conversations", label: i18n.t("reports.kpi.conversations"), align: "right" },
    { key: "resolved", label: i18n.t("reports.kpi.resolved"), align: "right" },
    {
      key: "rate",
      label: i18n.t("reports.team.rate"),
      align: "right",
      sort: (r) => percent(r.resolved, r.conversations),
      render: (r) => `${percent(r.resolved, r.conversations)}%`,
    },
    {
      key: "avgResolutionSeconds",
      label: i18n.t("reports.kpi.avgResolution"),
      align: "right",
      render: (r) => formatDuration(r.avgResolutionSeconds),
    },
  ];

  return (
    <div className={classes.grid}>
      <div className={classes.card}>
        <div className={classes.title}>{i18n.t("reports.charts.byChannel")}</div>
        <ChannelDonut rows={data.byChannel} />
      </div>
      <ReportTable title={i18n.t("reports.channels.title")} columns={columns} rows={rows} defaultSort="conversations" />
    </div>
  );
};

export default ChannelsTab;
